import React, { useMemo, useCallback, useState, useRef } from "react";

import FormattedValue from "@cayuse/shared-react.components.formatted-value";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import PagingControls from "@cayuse/shared-react.components.paging-controls";
import PropTypes from "prop-types";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import Icon from "@cayuse/shared-react.components.icon";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { generateId } from "@cayuse/shared-react.utils.helpers";
import { isNonEmptyArray, isFunction, isNonEmptyString, isBool } from "@cayuse/shared-react.utils.type-checks";

import ScreenReaderLabel from "components/ScreenReaderLabel";
import Table from "components/Table";
import { FIELD_TYPES, SELECT_ALL_KEY } from "globals/constants";
import useDictionary from "hooks/useDictionary";
import { getValueFromObjectByKeys } from "utils";


import styles from "./styles.css";

/**
 * The headers prop should be an array of key:label pairs, [{ key: "columnKey", label: "Display Value" }]
 * The "rows" prop should be an array of row values, [{ col1: "Display 1", col2: "Display 2", _isSelected: bool }]
 * 		- only columns with keys found in the headers array will be rendered
 * If using selectable rows, your row data should include an _isSelected key indicating the row's state
 * If pageLimit is boolean literal false, the paging controls will be omitted
 */
const FeedTable = ({
	tableid,
	headers,
	rows,
	totalItems,
	onPageChange,
	onPageLimitChange,
	onRowSelectionChange,
	overflow,
	pageLimit,
	pageOffset,
	isLoading,
	isMultiSelect,
	isRefresh,
	isSelectable,
	isSelectAll,
	isExpandable,
	accessibilityLabel,
	noResultsLabel,
	onRowClick,
	customClass,
	disabled,
	captionText,
	sort,
	onSortChange,
	renderCell,
}) => {
	const { table: dictionary } = useDictionary("common");
	const hasRows = isNonEmptyArray(rows);
	const hasHeaders = isNonEmptyArray(headers.filter(Boolean));
	const numColumns = hasHeaders ? headers.length + (isSelectable ? 1 : 0) + (isExpandable ? 1 : 0) : 0;
	const tableidAttribute = isNonEmptyString(tableid) ? { id: tableid } : {};
	const [ collapseAllSubRows, setCollapseAllSubRows ] = useState(false);
	const rowsToggler = useRef(0);

	const columnKeys = useMemo(() => {
		return hasHeaders
			? headers.map(({ id, keys, type }) => ({ id, keys, type }))
			: [];
	}, [ headers, hasHeaders ]);

	const getScreenReaderLabel = row => {
		const useColumnValue = isNonEmptyString((accessibilityLabel)) && accessibilityLabel in row;
		let label = "";

		if (useColumnValue) {
			label = row[accessibilityLabel];
		}

		if (isFunction(accessibilityLabel)) {
			label = accessibilityLabel(row);
		}

		return `${dictionary.selectRow || "Select row"} ${label}`;
	};

	const toggleSelectAll = useCallback(isSelected => {
		const shouldBeSelected = isBool(isSelected) ? isSelected : !isSelectAll;

		if (isFunction(onRowSelectionChange)) {
			onRowSelectionChange({ row: SELECT_ALL_KEY, isSelected: shouldBeSelected });
		}
	}, [ isSelectAll, onRowSelectionChange ]);

	const handleSelectRowClick = (row, isSelected) => {
		if (isFunction(onRowSelectionChange)) {
			onRowSelectionChange({ row, isSelected });
		}
	};
	const handleClick = row => {
		if (isFunction(onRowClick)) {
			onRowClick(row);
		}
	};

	const selectAllId = useMemo(() => generateId("select-all"), []);

	const renderCheckboxHeaderCell = isMultiSelect => (
		<Table.HeaderCell className={styles.checkbox}>
			{isMultiSelect ?
				<>
					<input type="checkbox"
						id={selectAllId}
						checked={isSelectAll}
						onChange={() => toggleSelectAll()}
					/>
					<ScreenReaderLabel htmlFor={selectAllId}>{dictionary.selectAll || "Select All"}</ScreenReaderLabel>
				</> :
				<>
						Selected
				</>
			}
		</Table.HeaderCell>
	);

	const renderSelectRowCheckbox = row => {
		const checkboxId = generateId("select-row");
		const screenReaderLabel = isSelectable && getScreenReaderLabel(row);
		let isSelected = false;
		if (isBool(row._isSelected)) {
			isSelected = row._isSelected;
		} else {
			isSelected = false;
			const warning = "No boolean value for _isSelected provided to renderSelectRowCheckbox. "
				+ "Selectable rows must have this value. Row data: %o";
			console.warn(warning, row);
		}

		return isSelectable ? (
			<Table.Cell className={styles.checkbox}>
				<input
					type="checkbox"
					id={checkboxId}
					checked={isSelected}
					onChange={event => handleSelectRowClick(row, event.target?.checked)}
				/>
				{isNonEmptyString(screenReaderLabel) &&
					<ScreenReaderLabel htmlFor={checkboxId}>{screenReaderLabel}</ScreenReaderLabel>
				}
			</Table.Cell>
		) : null;
	};

	const isExpandableRow = row => {
		return isNonEmptyArray(row.subRows);
	};

	const renderExpandAllRowsButton = () => (
		<Table.HeaderCell  className={styles.checkbox}>
			<CovertButton type="button" aria-expanded={collapseAllSubRows} ref={rowsToggler} onClick={showSubRows} title="Toggle all sub-rows">
				<Icon name={"chevron-right"}/>
				<span className="accessibility-text">Toggle</span>
			</CovertButton>
		</Table.HeaderCell>
	);

	const renderRowContent = row => columnKeys.map(({ keys, type, ...column }, colIndex) => {
		const rawValue = getValueFromObjectByKeys(row, keys);

		if (isFunction(renderCell)) {
			const customCell = renderCell({ keys, type, row, column, value: rawValue });
			if (customCell) {
				return <Table.Cell className={isFunction(onRowClick) ? styles.hover : ''} key={`row-${colIndex}`}>{customCell}</Table.Cell>;
			}
		}

		if (isNonEmptyString(rawValue) && rawValue.includes(",") && (type === FIELD_TYPES.DATE || type === FIELD_TYPES.DATETIME)) {
			return (
				<Table.Cell key={`row-${colIndex}`} className={isFunction(onRowClick) ? styles.hover : ''}>
					{rawValue
						.split(",")
						.map(s => s.trim())
						.map((v, index, source) => {
							return (
								<><FormattedValue value={v} type={type} fallback="" />{source.length - 1 !== index ? ", " : ""}</>
							);
						})}
				</Table.Cell>
			);
		}

		return (
			<Table.Cell key={`row-${colIndex}`} className={isFunction(onRowClick) ? styles.hover : ''}>
				{isNonEmptyString(rawValue)
					? <FormattedValue value={rawValue} type={type}/>
					: rawValue
				}
			</Table.Cell>
		);
	});

	const showSubRows = () => {
		setCollapseAllSubRows(!collapseAllSubRows);
		rowsToggler.current.firstChild.setAttribute("data-icon", collapseAllSubRows ? "chevron-right" : "chevron-down");
	};

	const renderSubRows = row => {
		const subRows = row.subRows;

		return(
			subRows.map((subRow, subRowIndex) => {
				const firstSubRowCell = subRowIndex === 0 ? styles.firstSubRowEmptyCell : styles.emptySubrowCell;

				return (
					<Table.Row key={`subRow-${subRowIndex}`} onClick={() => handleClick(subRow)}>
						<Table.Cell colSpan={isSelectable ? 2 : 1} className={`${styles.emptySpace} ${firstSubRowCell}`}/>
						{renderRowContent(subRow)}
					</Table.Row>
				);
			}
			));
	};

	const renderSelectRowRadio = row => {
		const radioId = generateId("select-row");
		const screenReaderLabel = isSelectable && getScreenReaderLabel(row);
		let isSelected;
		if (isBool(row._isSelected)) {
			isSelected = row._isSelected;
		} else {
			isSelected = false;
			const warning = "No boolean value for _isSelected provided to renderSelectRowRadio. "
				+ "Selectable rows must have this value. Row data: %o";
			console.warn(warning, row);
		}

		return (
			isSelectable ?
				<Table.Cell className={styles.checkbox}>
					<input type="radio"
					   id={radioId}
					   disabled={disabled}
					   checked={isSelected}
					   onChange={event => handleSelectRowClick(row, event.target?.checked)}
					/>
					{isNonEmptyString(screenReaderLabel) &&
				<ScreenReaderLabel htmlFor={radioId}>{screenReaderLabel}</ScreenReaderLabel>
					}
				</Table.Cell> : <Table.Cell />
		);
	};

	const renderSelector = (isMultiSelect, row) => {
		return isMultiSelect ?  renderSelectRowCheckbox(row) : renderSelectRowRadio(row);
	};

	return(
		<div>
			<VerticalSpacing>
				<VerticalSpacing>
					<div className={styles.feedTableWrapper}>
						<Table
							{...tableidAttribute}
							className={`${styles.feedTable} ${customClass}`}
							selectable
							overflow={overflow}
							captionText={captionText}
							sortable={!!sort}
							unstackable
						>
							<Table.Header>
								<Table.Row>
									{isExpandable && hasRows && renderExpandAllRowsButton()}
									{isSelectable && hasRows && renderCheckboxHeaderCell(isMultiSelect)}
									{hasHeaders && headers.map(({ id, columnHeader, sortKey, style }) => {
										const /**@type {'ascending' | 'descending' | null} */sortDirection =
												sortKey === sort?.column ? sort?.direction : null;
										const nextDirection = sort?.direction === "ascending" ? "descending" : "ascending";

										return (
											<Table.HeaderCell
												className={isNonEmptyString(sortKey) ? "sortable" : undefined}
												key={`header-${id}`}
												style={style}
												sorted={sortDirection}
												aria-sort={sortDirection}
											>
												{sort && isNonEmptyString(sortKey) ? (
													<button
														type="button"
														className={styles.sortButton}
														aria-label={`sort by ${columnHeader} in ${nextDirection} order`}
														onClick={event => {
															event.stopPropagation();
															onSortChange(sortKey, nextDirection);
														}}
													>
														{columnHeader}
													</button>
												) : columnHeader}
											</Table.HeaderCell>
										);
									})}
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{isLoading && !isRefresh
									? (
										<Table.Row disabled>
											<Table.Cell colSpan={numColumns}>
												<LoadingIndicator className={styles.loadingSpinner} />
											</Table.Cell>
										</Table.Row>
									) : hasRows ?
										rows.map((row, rowIndex) => (
											isExpandable && isExpandableRow(row)  ?
												<Table.ExpandableRow
													key={`row-${rowIndex}`}
													onClick={() => handleClick(row)}
													rowData={row}
													selectCheckbox={renderSelectRowCheckbox(row)}
													subContent={renderSubRows(row)}
													expandRow={collapseAllSubRows}
												>
													{renderRowContent(row)}
												</Table.ExpandableRow>
												:
												<Table.Row key={`row-${rowIndex}`} onClick={() => handleClick(row)}>
													{isExpandable && <Table.Cell className={styles.emptySpace}/>}
													{isSelectable && renderSelector(isMultiSelect, row)}
													{renderRowContent(row)}
												</Table.Row>
										))
										: (
											<Table.Row disabled>
												<Table.Cell colSpan={numColumns}>
													<div className={styles.noResults}>
														<span>
															{noResultsLabel || dictionary.noResults || "No Results"}
														</span>
													</div>
												</Table.Cell>
											</Table.Row>
										)
								}
							</Table.Body>
						</Table>
					</div>
				</VerticalSpacing>

				{pageLimit !== false && hasRows && !isLoading && (
					<div className={styles.pagingControls}>
						<PagingControls
							items={rows}
							totalItems={totalItems}
							pageLimit={pageLimit}
							pageOffset={pageOffset}
							onPageChange={onPageChange}
							onPageSizeChange={onPageLimitChange}
						/>
					</div>
				)}
			</VerticalSpacing>
		</div>
	);
};

FeedTable.defaultProps = {
	overflow: "hidden",
	isMultiSelect: true,
};

FeedTable.propTypes = {
	tableid: PropTypes.string,
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			keys: PropTypes.arrayOf(PropTypes.string),
			id: PropTypes.string,
			columnHeader: PropTypes.string
		}
		)),
	rows: PropTypes.arrayOf(PropTypes.shape({
		key: PropTypes.string,
		content: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.number,
			PropTypes.node
		])
	})),
	totalItems: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	accessibilityLabel: PropTypes.oneOfType([ PropTypes.string, PropTypes.func ]),
	pageLimit: PropTypes.oneOfType([ PropTypes.number, PropTypes.string, PropTypes.bool ]),
	pageOffset: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
	onPageChange: PropTypes.func,
	onPageLimitChange: PropTypes.func,
	onRowSelectionChange: PropTypes.func,
	overflow: PropTypes.oneOf([ "hidden", "visible" ]),
	isLoading: PropTypes.bool,
	isMultiSelect: PropTypes.bool,
	isSelectable: PropTypes.bool,
	isSelectAll: PropTypes.bool,
	captionText: PropTypes.string
};

export default FeedTable;
